import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Moon, Sun, Menu, X, LogOut, User as UserIcon, LayoutDashboard, Search, Home } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Accueil", exact: true },
  { to: "/annuaire", label: "Annuaire" },
  { to: "/comment-ca-marche", label: "Comment ça marche" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/courtiers", label: "Courtiers" },
  { to: "/guides", label: "Guides" },
  { to: "/faq", label: "FAQ" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);

  const dashboardPath = user?.role === "admin" ? "/admin" : user?.role === "broker" ? "/courtier" : "/espace";

  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="h-9 w-9 rounded-lg bg-gradient-brand flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M4 8L12 3L20 8V16L12 21L4 16V8Z" stroke="white" strokeWidth="2" strokeLinejoin="round"/>
                <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-semibold tracking-tight text-[15px]">Résiliation Express</span>
              <span className="text-[10px] text-muted-foreground tracking-widest uppercase">Legaltech · France</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={`nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 inline-flex items-center gap-1 ${
                    isActive
                      ? "text-foreground bg-secondary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                {n.to === "/" && <Home className="h-3.5 w-3.5" />}
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-btn" aria-label="Basculer thème">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {!user ? (
              <>
                <Button asChild variant="ghost" size="sm" data-testid="header-login-btn">
                  <Link to="/connexion">Connexion</Link>
                </Button>
                <Button asChild size="sm" className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="header-register-btn">
                  <Link to="/inscription">Créer un compte</Link>
                </Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="user-menu-btn">
                    <UserIcon className="h-4 w-4 mr-2" />
                    {user.first_name || user.email.split("@")[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="menu-dashboard">
                    <Link to={dashboardPath}><LayoutDashboard className="mr-2 h-4 w-4" />Mon espace</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-new-case">
                    <Link to="/resilier"><Search className="mr-2 h-4 w-4" />Nouvelle résiliation</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-4 flex flex-col gap-1 border-t border-border pt-3" data-testid="mobile-nav">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200"
              >
                {n.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
